import { useMemo, useState, useCallback, useRef } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';

import { BellIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { Badge, Heading, Tooltip, Button, Spinner } from '@restworld/ui-ds-base';
import { Address, Variable, WorkerJPSavedResearch } from '@restworld/utility-types';
import {
  defaultItalyAddressMetaData,
  useNotificationFrequencyOptions,
  useResponsiveBreakPoints,
} from '@restworld/utils-common';
import { useWorkersService } from '@restworld/data-services';

import {
  notificationModalStateAtom,
  notificationModalToggleAtom,
  notificationResearchIdAtom,
  SaveResearchFrequency,
  notificationChangeAtom,
  userUnsubscribeReasonAtom,
  userUnsubscribeReasonFreeTextAtom,
  savedResearchDataAtom,
  occupationDataAtom,
} from './save-research-tab.atoms';
import SaveResearchSubscribeToggle from './save-research-subscribe-toggle';

const SavedResearch = ({
  data,
  domainInterest,
  onTitleUpdate,
}: {
  data: Partial<WorkerJPSavedResearch>;
  domainInterest?: Variable[];
  onTitleUpdate?: (id: number, title: string) => void;
}) => {
  const [loading, onLoading] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState(data.title || '');
  const titleInputRef = useRef<HTMLInputElement>(null);

  const setNotificationToggle = useSetAtom(notificationModalToggleAtom);
  const setNotificationResearchId = useSetAtom(notificationResearchIdAtom);
  const setNotificationModalState = useSetAtom(notificationModalStateAtom);
  const setNotificationChange = useSetAtom(notificationChangeAtom);
  const setUserUnsubscribeReason = useSetAtom(userUnsubscribeReasonAtom);
  const setUserUnsubscribeReasonText = useSetAtom(userUnsubscribeReasonFreeTextAtom);
  const setSavedResearch = useSetAtom(savedResearchDataAtom);
  const [{ data: occupationData }] = useAtom(occupationDataAtom);
  const { t } = useTranslation();

  const notificationFrequencyOptions = useNotificationFrequencyOptions();
  const { isDesktop } = useResponsiveBreakPoints();

  const filterParams = useMemo(() => data.filter_params, [data.filter_params]);
  const address = useMemo(() => filterParams?.address as Address, [filterParams?.address]);
  const isSeasonalAccommodation = useMemo(
    () => filterParams?.contract_type?.includes('seasonal_accommodation'),
    [filterParams?.contract_type]
  );

  const domainInterestIds = useMemo(() => filterParams?.domain_interest as string[], [filterParams?.domain_interest]);

  const domainInterestFirstIndex = useMemo(() => {
    const names = domainInterestIds?.map((id) => domainInterest?.find((item) => item.id === id)?.label);
    return names?.length ? names[0] : '';
  }, [domainInterest, domainInterestIds]);

  const occupationIds = useMemo(() => (filterParams?.['occupation_id'] as string[]) ?? [], [filterParams]);

  const occupationIdsFirstIndex = useMemo(() => {
    const names = occupationIds?.map((id) => occupationData?.find((item) => item.id === Number(id))?.label_it?.label);
    return names?.length ? names[0] : '';
  }, [occupationData, occupationIds]);

  const frequencyText = useMemo(
    () =>
      data.enabled
        ? notificationFrequencyOptions.find((item) => item.id === data.frequency)?.label
        : t('common:deactivate'),
    [t, data.enabled, data.frequency, notificationFrequencyOptions]
  );

  const frequencyEl = useMemo(() => {
    const title = (
      <span className="flex flex-row items-center gap-1.5 leading-5.25">
        <BellIcon className="h-6 w-6 text-gray-500" />
        <Heading size={6} color="muted-foreground" weight="normal" maxChars={!isDesktop ? 10 : 40}>
          {frequencyText}
        </Heading>
      </span>
    );
    if (frequencyText.length > 10 && !isDesktop)
      return (
        <Tooltip style="dark" content={frequencyText}>
          {title}
        </Tooltip>
      );
    return title;
  }, [frequencyText, isDesktop]);

  const onModalOpen = useCallback(() => {
    setSavedResearch(data);
    setNotificationResearchId(data.id);
    // reset previous the modal states
    setNotificationModalState('default');
    setNotificationChange(data.frequency as SaveResearchFrequency);
    setUserUnsubscribeReason('');
    setUserUnsubscribeReasonText('');
    // open the modal
    setNotificationToggle(true);
  }, [
    setNotificationResearchId,
    data,
    setSavedResearch,
    setNotificationModalState,
    setNotificationChange,
    setUserUnsubscribeReason,
    setUserUnsubscribeReasonText,
    setNotificationToggle,
  ]);
  const addressTitle = useMemo(
    () =>
      address?.locality
        ? address.locality === defaultItalyAddressMetaData.locality || address.locality === 'Italy'
          ? t('common:Italy')
          : address.locality
        : address?.address,
    [address?.address, address?.locality, t]
  );
  const generatedTitle = useMemo(
    () =>
      address
        ? occupationIdsFirstIndex
          ? `${occupationIdsFirstIndex} a ${addressTitle}`
          : addressTitle
        : occupationIdsFirstIndex ||
          `${t('user-settings:saved_search_title', {
            id: `#${data.id}`,
          })}`,
    [address, addressTitle, data.id, occupationIdsFirstIndex, t]
  );

  const title = data.title || generatedTitle;

  const handleTitleSave = useCallback(() => {
    const trimmed = editedTitle.trim();
    if (trimmed && trimmed !== data.title && data.id) {
      onTitleUpdate?.(data.id, trimmed);
    }
    setIsEditingTitle(false);
  }, [editedTitle, data.title, data.id, onTitleUpdate]);

  const handleTitleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') handleTitleSave();
      if (e.key === 'Escape') {
        setEditedTitle(data.title || '');
        setIsEditingTitle(false);
      }
    },
    [handleTitleSave, data.title]
  );

  return (
    <div className="flex flex-col gap-5 lg:min-w-[500px] relative">
      <div className="flex flex-col gap-2">
        {isEditingTitle ? (
          <input
            ref={titleInputRef}
            value={editedTitle}
            onChange={(e) => setEditedTitle(e.target.value)}
            onBlur={handleTitleSave}
            onKeyDown={handleTitleKeyDown}
            autoFocus
            maxLength={255}
            className="text-base font-semibold leading-6 border border-gray-300 rounded px-2 py-1 outline-none focus:border-primary-500"
          />
        ) : (
          <div
            className="group flex items-center gap-1 cursor-pointer"
            onClick={() => {
              setEditedTitle(title);
              setIsEditingTitle(true);
            }}
          >
            <Heading size={5} className="leading-6">
              {title}
            </Heading>
            <span className="text-gray-400 opacity-0 group-hover:opacity-100 text-xs">✏</span>
          </div>
        )}
        <div className="flex flex-row justify-between items-center">
          <div className="flex flex-row gap-2 flex-wrap">
            {domainInterestFirstIndex && <Badge variant="primary">{domainInterestFirstIndex}</Badge>}
            {occupationIdsFirstIndex && <Badge variant="primary">{occupationIdsFirstIndex}</Badge>}
            {address?.locality && <Badge variant="primary">{addressTitle}</Badge>}
            {isSeasonalAccommodation && <Badge variant="primary">{t('common:seasonal_accommodation')}</Badge>}
            {domainInterestIds?.length && (
              <Badge variant="primary">+{occupationIds?.length + domainInterestIds.length}</Badge>
            )}
            {!domainInterestFirstIndex &&
              !occupationIdsFirstIndex &&
              !address?.locality &&
              !isSeasonalAccommodation && (
                <span className="flex flex-row items-center gap-1.5">
                  <InformationCircleIcon className="h-6" />
                  <Heading size={6} color="muted-foreground" weight="normal">
                    {t('user-settings:no_search_criteria')}
                  </Heading>
                </span>
              )}
          </div>
          <SaveResearchSubscribeToggle onLoading={onLoading} defaultChecked={data.enabled} saveResearchId={data.id} />
        </div>
      </div>
      <div className="flex gap-2 flex-row items-center justify-between">
        <Button onClick={onModalOpen} variant="tertiary">
          {t('user-settings:edit_search')}
        </Button>
        {frequencyEl}
      </div>
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90 z-50">
          <Spinner color="primary" size="md" />
        </div>
      )}
    </div>
  );
};
export default SavedResearch;
